export const LOGIN_METHODS = {
  EMAIL: "email",
  GOOGLE: "google",
  TWITTER: "twitter",
  FACEBOOK: "facebook",
  CUSTOM_SSO: "custom_sso",
} as const;

const KNOWN_METHODS = Object.values(LOGIN_METHODS);

export type LoginMethodKey = (typeof KNOWN_METHODS)[number];

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const cookieName = (communityId?: number) => {
  const suffix = communityId ? `_${communityId}` : "";
  return `circle_last_login_method${suffix}`;
};

const pendingCookieName = (communityId?: number) => {
  const suffix = communityId ? `_${communityId}` : "";
  return `circle_pending_login_method${suffix}`;
};

const parentDomain = (hostname: string): string | null => {
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;
  if (hostname === "localhost" || !hostname.includes(".")) return null;
  const parts = hostname.split(".");
  if (parts.length < 2) return null;
  return `.${parts.slice(-2).join(".")}`;
};

const writeCookie = (name: string, value: string) => {
  const domain = parentDomain(window.location.hostname);
  const domainAttr = domain ? `; Domain=${domain}` : "";
  const secureAttr = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${domainAttr}${secureAttr}`;
};

const readCookie = (name: string): string | null => {
  const match = document.cookie
    .split("; ")
    .find(row => row.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.slice(name.length + 1);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const deleteCookie = (name: string) => {
  const domain = parentDomain(window.location.hostname);
  const domainAttr = domain ? `; Domain=${domain}` : "";
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${domainAttr}`;
};

export const saveLastLoginMethod = (
  method: LoginMethodKey,
  communityId?: number,
) => {
  if (!KNOWN_METHODS.includes(method)) return;
  writeCookie(cookieName(communityId), method);
};

export const markLoginMethodPending = (
  method: LoginMethodKey,
  communityId?: number,
) => {
  if (!KNOWN_METHODS.includes(method)) return;
  writeCookie(pendingCookieName(communityId), method);
};

export const confirmPendingLoginMethod = (communityId?: number) => {
  const pending = readCookie(pendingCookieName(communityId));
  if (pending && KNOWN_METHODS.includes(pending as LoginMethodKey)) {
    writeCookie(cookieName(communityId), pending);
  }
  deleteCookie(pendingCookieName(communityId));
};

const getLastLoginMethod = (communityId?: number): LoginMethodKey | null => {
  const value = readCookie(cookieName(communityId));
  if (value && KNOWN_METHODS.includes(value as LoginMethodKey)) {
    return value as LoginMethodKey;
  }
  return null;
};

export const isLastLoginMethod = (
  method: LoginMethodKey,
  communityId?: number,
): boolean => getLastLoginMethod(communityId) === method;
